/**
 * Warm the payments partner's SANDBOX origin for a test-mode checkout.
 *
 * Live checkouts get `preconnect` / `dns-prefetch` hints for the partner's
 * production origin straight from index.html, so the payment frame's
 * connection is open before it mounts. A test-mode checkout loads the frame
 * from the sandbox origin instead, which had no hint: the frame paid a cold
 * DNS + TCP + TLS setup after the session resolved. Production telemetry
 * (checkout loads, 2026-10-19): test-mode frames took 2.2–6.9s from mount to
 * ready, against ~0.5s for the live frame.
 *
 * Added the moment test mode is known — from the early product fetch
 * (checkoutEarlyIntent.ts), well before the session resolves — and again from
 * the checkout itself for flows the early entry does not cover
 * (subscriptions). Idempotent. Kept dependency-free: the early entry imports
 * it.
 */

export const PARTNER_SANDBOX_ORIGIN = "https://sandbox.whop.com";

const MARKER = "data-pf-sandbox-warm";

/** Adds the sandbox origin hints once per document. True when it added them. */
export function warmPartnerSandbox(
	doc: Document | undefined = typeof document === "undefined"
		? undefined
		: document,
): boolean {
	if (!doc?.head) return false;
	if (doc.head.querySelector(`link[${MARKER}]`)) return false;
	for (const rel of ["preconnect", "dns-prefetch"]) {
		const link = doc.createElement("link");
		link.rel = rel;
		// The frame is a navigation, not a CORS fetch: no crossorigin, so the
		// warmed connection is the one the frame uses.
		link.href = PARTNER_SANDBOX_ORIGIN;
		link.setAttribute(MARKER, "");
		doc.head.appendChild(link);
	}
	return true;
}
